import { DatasetController, Element } from 'chart.js';
import { valueOrDefault } from 'chart.js/helpers/helpers';
import { format as numFormat } from '@awp/helpers/numFormat';

import bilinear from './bilinear';

const TEMP_PROFILE_ID = 'TemperatureProfile';

function getFirstScaleId(chart, axis) {
  const scales = chart.scales;
  return Object.keys(scales)
    .filter((key) => scales[key].axis === axis)
    .shift();
}

export class TemperatureProfileElement extends Element {
  static id = TEMP_PROFILE_ID;

  inRange() {
    return false;
  }
}

// This is where all the action happens to display the Frost Probe graph.
export class TemperatureProfileController extends DatasetController {
  static id = TEMP_PROFILE_ID;
  static defaults = {
    datasetElementType: TEMP_PROFILE_ID,
    dataElementType: TEMP_PROFILE_ID,
    minRange: -1,
    maxRange: 1,
  };

  invalidStyle(options) {
    const defaultOptions = {
      size: 8,
      lineWidth: 2,
      color: '#ffffff',
    };

    const { size, lineWidth, color } = Object.assign({}, defaultOptions, options);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = size;
    canvas.height = size;
    ctx.lineCap = 'round';
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = color;

    ctx.beginPath();

    for (let i = 0; i < 3; i++) {
      const a = (size / 2) * (i - 1);
      ctx.moveTo(a, size - 1 + a);
      ctx.lineTo(size - 1 + a, a);
    }
    ctx.stroke();

    return ctx.createPattern(canvas, 'repeat');
  }

  draw() {
    const { ctx, chartArea, data } = this.chart;
    const { minRange, maxRange } = this.options;
    const dataset = data.datasets.find((dataset) => dataset.type === TEMP_PROFILE_ID);
    const pixelRatio = window.devicePixelRatio || 1;
    const xScale = this.chart.scales[dataset.xAxisID || 'x'];
    const yScale = this.chart.scales[dataset.yAxisID || 'y'];
    const width = Math.round((chartArea.right - chartArea.left) * pixelRatio);
    const height = Math.round(chartArea.height * pixelRatio);
    const left = Math.round(chartArea.left * pixelRatio);
    const top = Math.round(chartArea.top * pixelRatio);
    const imageData = ctx.getImageData(left, top, width, height);
    const buffer = new Uint8Array(imageData.data.buffer);
    const points = [];
    const dX = [];
    let dY = [];
    let ys = [];
    const invalid = [];
    let plotData = null;
    let prevX = null;
    //let prevY = null;
    let startX = 0;
    let offsetX = 0;

    for (let i = 0; i < dataset.data.length; i++) {
      const x = Math.round(xScale.getPixelForValue(dataset.data[i][0]) * pixelRatio - left);

      if (prevX === null) {
        if (i === 0) {
          startX = x;
        }

        ys = yScale.options.thermistorDepths.map((td) => {
          points.push([]);
          return Math.round(yScale.getPixelForValue(td) * pixelRatio - top);
        });
        dY = ys
          .map((y, i, a) => {
            if (i !== 0) {
              return Math.max(0, y - a[i - 1] - 1);
            }
          })
          .filter((a) => a !== undefined);
        // for (let j = 0; j < dataset.data[i][1].length; j++) {
        // 	const y = Math.round(yScale.getPixelForValue(yScale.options.thermistorDepths[j]) * pixelRatio - top);

        // 	if (prevY !== null) {
        // 		dY.push(Math.max(0, y - prevY - 1));
        // 	}

        // 	points.push([]);
        // 	ys.push(y);
        // 	prevY = y;
        // }

        // If start datapoint does not align with left side plot area we'll need the previous datapoint as well
        if (x > 0 && i > 0) {
          offsetX = Math.round(xScale.getPixelForValue(dataset.data[i - 1][0]));
          dX.push(Math.max(0, x - offsetX - 1));
          for (let j = 0; j < dataset.data[i - 1][1].length; j++) {
            if (!yScale.options.showInvalidData && dataset.data[i - 1][1][j].isValid) points[j].push(null);
            else points[j].push(dataset.data[i - 1][1][j].value);
          }
        }
      } else {
        dX.push(Math.max(0, x - prevX - 1));
      }

      for (let j = 0; j < dataset.data[i][1].length; j++) {
        const value = Number.isNaN(dataset.data[i][1][j].value) ? null : dataset.data[i][1][j].value;
        //sometimes the system tries to push to the points[j] array when it doesn't exist?
        if (points[j]) {
          if (!dataset.data[i][1][j].isValid) {
            if (yScale.options.showInvalidData) {
              invalid.push({ x, y: ys[j], i: dX.length - 1, j });
              points[j].push(value);
            } else points[j].push(null);
          } else {
            points[j].push(value);
          }
        }
      }

      if (dataset.data[i][0] > xScale.max) {
        break;
      }

      prevX = x;
    }

    // const pointsWithCoords = points.map((p, i) => {
    // 	return {pointValue: p, y: ys[i]}
    // })

    // Fill in the space between thermistor measurements using bilinear interpolation.
    plotData = bilinear(points, dX, dY);

    // Write pixels to image buffer
    for (let y = 0; y < plotData.length; y++) {
      let i = (y * width + startX) * 4;
      for (let x = -offsetX; x < Math.min(width - offsetX - startX, plotData[y].length - 1); x++) {
        if (plotData[y][x] !== null) {
          // Scale and clamp plotData values so that [minRange, maxRange] => [0, 1], then map to color gradient
          let v = Math.min(1, Math.max(0, (plotData[y][x] - minRange) / (maxRange - minRange)));
          buffer[i++] /* r */ = 575.23 * v * v * v - 357.03 * v * v - 34.577 * v + 70.85;
          buffer[i++] /* g */ = -126.32 * v * v + 360.17 * v;
          buffer[i++] /* b */ = -339.05 * v * v + 277.41 * v + 86;
          buffer[i++] /* a */ = 0xff;
        } else {
          i += 4;
          continue;
        }
      }
    }

    // Draw dataset to canvas
    ctx.putImageData(imageData, left, top);

    // Add overlay to any regions with confidence level < 0 to visually mark them as invalid.
    if (yScale.options.showInvalidData) {
      const invalidOverlayCanvas = document.createElement('canvas');
      invalidOverlayCanvas.height = height;
      invalidOverlayCanvas.width = width;
      const invalidOverlayContext = invalidOverlayCanvas.getContext('2d');
      invalidOverlayContext.fillStyle = this.invalidStyle();

      for (let k = 0; k < invalid.length; k++) {
        const { x, y, i, j } = invalid[k];
        let w = (i > 0 ? dX[i - 1] : dX[i]) + dX[i] + 1;
        let h = (j > 0 ? dY[j - 1] : dY[j]) + dY[j];
        invalidOverlayContext.fillRect(
          (x - dX[i]) / pixelRatio,
          (y - dY[j] + 1) / pixelRatio,
          w / pixelRatio,
          h / pixelRatio,
        );
      }

      ctx.drawImage(invalidOverlayCanvas, left / pixelRatio, top / pixelRatio);
    }
  }

  /**
   * Override data parsing, since we are not using scales
   */
  parse(start, count) {
    const data = this.getDataset().data;
    const meta = this._cachedMeta;

    const yScale = this.chart.scales[meta._dataset.yAxisID || 'y'];
    const depths = yScale.options.thermistorDepths;

    if (this._parsing === false) {
      //meta._parsed = data;
    } else {
      const parsed = new Array(count);
      let i, ilen, index, item;
      for (i = 0, ilen = count; i < ilen; ++i) {
        index = i + start;
        item = data[index];

        parsed[i] = {
          x: parseInt(item[0]), //timestamp
          layer: item[1].map((v, i) => {
            let val = Number.isNaN(v.value) ? null : v.value;
            val = v.displayFormat && v.displayFormat.length > 0 && val !== null ? numFormat(val, v.displayFormat) : val;

            return {
              value: val,
              depth: Number.isNaN(v.elevation) ? depths[i] : v.elevation,
              isMissing: v.isMissing,
              isNan: v.isNan,
              isValid: v.isValid,
            };
          }),
        };
      }

      meta._parsed = parsed;
    }
  }

  linkScales() {
    const chart = this.chart;
    const meta = this._cachedMeta;
    const dataset = this.getDataset();

    const chooseId = (axis, x, y, r) => (axis === 'x' ? x : axis === 'r' ? r : y);

    const xid = (meta.xAxisID = valueOrDefault(dataset.xAxisID, getFirstScaleId(chart, 'x')));
    const yid = (meta.yAxisID = valueOrDefault(dataset.yAxisID, getFirstScaleId(chart, 'y')));
    const rid = (meta.rAxisID = valueOrDefault(dataset.rAxisID, getFirstScaleId(chart, 'r')));
    const indexAxis = meta.indexAxis;
    const iid = (meta.iAxisID = chooseId(indexAxis, xid, yid, rid));
    const vid = (meta.vAxisID = chooseId(indexAxis, yid, xid, rid));
    meta.xScale = this.getScaleForId(xid);
    meta.yScale = this.getScaleForId(yid);
    meta.rScale = this.getScaleForId(rid);
    meta.iScale = this.getScaleForId(iid);
    meta.vScale = this.getScaleForId(vid);
  }

  getLabelAndValue(index) {
    //index is a composite of data element index and thermistor depth index
    const dataIdx = index >>> 16;
    const tdIdx = index & 0xffff;

    const meta = this._cachedMeta;
    const iScale = meta.iScale;
    const vScale = meta.vScale;
    const parsed = this.getParsed(dataIdx);

    return {
      label: iScale && parsed[iScale.axis] ? '' + iScale.getLabelForValue(parsed[iScale.axis]) : '',
      value: vScale ? '' + vScale.getLabelForValue(parsed.layer[tdIdx]?.value || 0) : '',
      //label: iScale ? '' + iScale.getLabelForValue(parsedDepth[iScale.axis]) : '',
      //value: vScale ? '' + vScale.getLabelForValue(parsedDepth[vScale.axis]) : ''
    };
  }

  // docs say to implement this...
  update(mode) {
    const me = this;
    const meta = me._cachedMeta;

    //me.updateElements(meta.data, 0, meta.data.length, mode);
  }
}
